import { useEffect, useRef, useState } from 'react';
import { Modal } from 'antd';
import BuildDrawer from './BuildDrawer';
import { INITIAL_BUILD_STATES, STAGE_NAME } from './buildConst';

/**
 * 构建抽屉逻辑（列表页 / 详情页共用）
 * 返回：buildStates（各 `项目:阶段` 状态）、openBuild（发起/查看）、drawerElement（抽屉节点）
 */
export const useBuildDrawer = (initialStates = INITIAL_BUILD_STATES) => {
    const [buildStates, setBuildStates] = useState(initialStates);
    const [drawer, setDrawer] = useState({ open: false, record: null, stage: null });
    const [progress, setProgress] = useState(0);
    const [nonce, setNonce] = useState(0);
    const doneRef = useRef(new Set());
    const failedRef = useRef(new Set());
    const key = drawer.record ? `${drawer.record.id}:${drawer.stage}` : null;

    useEffect(() => {
        if (!drawer.open || !key) return undefined;
        if (doneRef.current.has(key)) { setProgress(100); return undefined; }
        if (failedRef.current.has(key)) { setProgress(60); return undefined; }
        setProgress(0);
        setBuildStates((p) => ({ ...p, [key]: 'building' }));
        let pr = 0;
        const timer = setInterval(() => {
            pr = Math.min(100, pr + 5);
            setProgress(pr);
            if (pr >= 100) {
                clearInterval(timer);
                doneRef.current.add(key);
                setBuildStates((prev) => ({ ...prev, [key]: 'done' }));
            }
        }, 300);
        return () => clearInterval(timer);
    }, [drawer.open, key, nonce]);

    const start = (record, stage) => {
        const k = `${record.id}:${stage}`;
        const st = buildStates[k];
        if (st === 'done') doneRef.current.add(k);
        if (st === 'failed') failedRef.current.add(k);
        setDrawer({ open: true, record, stage });
    };

    // 发起「构建一/二阶段」或失败后「重新构建」前二次确认；查看产物 / 查看进度直接打开
    const openBuild = (record, stage) => {
        const st = buildStates[`${record.id}:${stage}`];
        if (st === 'done' || st === 'building') { start(record, stage); return; }
        Modal.confirm({
            title: `构建${STAGE_NAME[stage]}`,
            content: `确认执行云端构建App应用${STAGE_NAME[stage]}吗？`,
            okText: '确认构建',
            cancelText: '取消',
            onOk: () => start(record, stage),
        });
    };

    const close = () => setDrawer({ open: false, record: null, stage: null });

    const rebuild = () => {
        if (!key) return;
        doneRef.current.delete(key);
        failedRef.current.delete(key);
        setBuildStates((p) => ({ ...p, [key]: 'building' }));
        setNonce((n) => n + 1);
    };

    const drawerElement = (
        <BuildDrawer
            open={drawer.open}
            record={drawer.record}
            stage={drawer.stage}
            progress={progress}
            stateVal={key ? buildStates[key] : null}
            onClose={close}
            onRebuild={rebuild}
        />
    );

    return { buildStates, openBuild, drawerElement };
};

export default useBuildDrawer;
